/** GPU Shadows/Highlights — fragment-shader port of
 *  `lib/adjustments/shadows-highlights.ts`.
 *
 *  Power-curve luminance bands:
 *    shadowsW    = (1 - L)^k   (k narrows the band as it grows)
 *    highlightsW = L^k
 *  Exponent k derives from TonalWidth: 100 → 1 (broad),
 *  0 → 5 (narrow), 50 → 3 (default).
 *
 *  Per-channel application sums shadow + highlight signed shifts
 *  and blends toward 1 (positive) or 0 (negative). No spatial
 *  radius — pure per-pixel like the CPU path (Photoshop's Radius
 *  would require a luminance-blur pre-pass and lands with the
 *  next-PR convolutions). */

import { LUMA_GLSL } from '../glsl-helpers';
import type { ShadowsHighlightsValue } from '../../adjustments/shadows-highlights';
import { applyShader } from '../runtime';

const SHADOWS_HIGHLIGHTS_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform float u_sK;       // shadow exponent (1..5)
uniform float u_hK;       // highlight exponent (1..5)
uniform float u_sAmt;     // shadow shift, normalized [-1, 1]
uniform float u_hAmt;     // highlight shift, normalized [-1, 1]
out vec4 outColor;
${LUMA_GLSL}

void main() {
  vec4 c = texture(u_source, v_uv);
  float L = LUMA_R * c.r + LUMA_G * c.g + LUMA_B * c.b;
  float sW = pow(1.0 - L, u_sK);
  float hW = pow(L, u_hK);
  float shift = u_sAmt * sW + u_hAmt * hW;
  vec3 rgb;
  if (shift > 0.0) rgb = c.rgb + (1.0 - c.rgb) * shift;
  else if (shift < 0.0) rgb = c.rgb + c.rgb * shift;
  else rgb = c.rgb;
  rgb = clamp(rgb, 0.0, 1.0);
  rgb = floor(rgb * 255.0 + 0.5) / 255.0;
  outColor = vec4(rgb, c.a);
}
`;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function applyShadowsHighlightsOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ShadowsHighlightsValue,
): boolean {
  // Same exponent derivation as the JS path.
  const sK = 1 + (1 - clamp01(value.shadowsTonalWidth / 100)) * 4;
  const hK = 1 + (1 - clamp01(value.highlightsTonalWidth / 100)) * 4;
  return applyShader({
    source,
    target,
    fragShaderSrc: SHADOWS_HIGHLIGHTS_FRAG,
    uniforms: {
      u_sK: { type: 'float', value: sK },
      u_hK: { type: 'float', value: hK },
      u_sAmt: { type: 'float', value: value.shadowsAmount / 100 },
      u_hAmt: { type: 'float', value: value.highlightsAmount / 100 },
    },
  });
}
